import classNames from "classnames";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Hint from "../components/Hint";
import { ErrorState, Loading, PendingTag, StagePill } from "../components/Visuals";
import { area, date, daysFromToday, LEAD_UNIT, otherUnit, SCHEDULE_LABELS, scheduleStatus } from "../format";
import { AmendmentRef, UnitHistoryResponse, UnitLease } from "../types";
import { useDashboardData } from "../useDashboardData";

const LEASE_STATUS: { [key in UnitLease["status"]]: string } = {
	current: "Current",
	future: "Upcoming",
	ended: "Ended"
};

const describe = (ref: AmendmentRef | null) => (ref && ref.type ? `${ref.type}${ref.id ? ` #${ref.id}` : ""}` : null);

const UnitDrawer = ({ unitId, onClose }: { unitId: string; onClose: () => void }) => {
	const { data, isLoading, error } = useDashboardData<UnitHistoryResponse>(`units/${unitId}`, {});

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<div className="ed-drawer" role="dialog" aria-label="Unit lease history">
			<div className="ed-drawer__backdrop" onClick={onClose} />
			<div className="ed-drawer__panel">
				{isLoading && <Loading />}
				{error && <ErrorState />}
				{data && <UnitHistory data={data} onClose={onClose} />}
			</div>
		</div>,
		document.body
	);
};

const UnitHistory = ({ data, onClose }: { data: UnitHistoryResponse; onClose: () => void }) => {
	const { unit, leases } = data;
	const days = unit.vacating ? daysFromToday(unit.vacating.date, data.as_of) : null;
	return (
		<React.Fragment>
			<header className="ed-drawer__header">
				<div>
					<div className="ed-eyebrow">Unit · {unit.property.short_name}</div>
					<h2 className="ed-card__title">{unit.code}</h2>
					<div className="ed-subtext">{[unit.property.name, unit.zone, unit.sub_zone].filter(Boolean).join(" · ")}</div>
				</div>
				<button type="button" className="ed-drawer__close" onClick={onClose} aria-label="Close">
					×
				</button>
			</header>

			<div className="ed-facts">
				<div className="ed-facts__item">
					<div className="ed-eyebrow">Status</div>
					<StagePill stage={scheduleStatus(unit)} label={SCHEDULE_LABELS[scheduleStatus(unit)]} />
				</div>
				<Hint
					block
					className="ed-facts__item"
					explain={{
						title: "Lease area",
						lines: [
							"Lease Area SQM and SQFT from Yardi unit master data, as in force today.",
							unit.area_as_of ? `Measured as of ${date(unit.area_as_of)}.` : null,
							unit.actual_area ? `Actual area: ${area(unit.actual_area)} · ${area(unit.actual_area, otherUnit(LEAD_UNIT))}` : null
						]
					}}
				>
					<div className="ed-eyebrow">GLA</div>
					<div className="ed-strong">{area(unit.gla)}</div>
					<div className="ed-subtext">{area(unit.gla, otherUnit(LEAD_UNIT))}</div>
				</Hint>
				<div className="ed-facts__item">
					<div className="ed-eyebrow">Tenant today</div>
					<div className="ed-strong">{unit.tenant_name || "–"}</div>
				</div>
			</div>

			{unit.vacating && (
				<div className={classNames("ed-outcome-box", unit.vacating.kind === "terminating" ? "ed-outcome-box--late" : "ed-outcome-box--marginal")}>
					<div className="ed-eyebrow">{unit.vacating.kind === "terminating" ? "Terminating" : "Floating"}</div>
					<div className="ed-outcome-box__value">
						{unit.vacating.reason} · {date(unit.vacating.date)}
					</div>
					<div className="ed-subtext">{days !== null ? `In ${days} days` : ""}</div>
				</div>
			)}

			<div className="ed-eyebrow">Lease history</div>
			{leases.length === 0 ? (
				<div className="ed-empty">No leases recorded for this unit.</div>
			) : (
				<ol className="ed-timeline">
					{leases.map((lease, index) => (
						<li key={`${lease.tcode}-${lease.start_date}-${index}`} className={`ed-timeline__item ed-timeline__item--${lease.status}`}>
							<span className="ed-timeline__dot" />
							<div className="ed-timeline__head">
								<span className="ed-strong">{lease.tenant_name || "Unknown tenant"}</span>
								<span className={`ed-lease-status ed-lease-status--${lease.status}`}>{LEASE_STATUS[lease.status]}</span>
							</div>
							<div className="ed-timeline__dates">
								{date(lease.start_date)} → {lease.end_date ? date(lease.end_date) : "open-ended"}
							</div>
							<div className="ed-subtext">
								{[
									lease.tcode,
									describe(lease.opened_by) && `Started by ${describe(lease.opened_by)}`,
									describe(lease.closed_by) && `Ended by ${describe(lease.closed_by)}`,
									lease.source === "tcp" ? "TCP lease record" : null
								]
									.filter(Boolean)
									.join(" · ")}
							</div>
							<div className="ed-timeline__links">
								{lease.project && (
									<span>
										<Link to={`/projects/${lease.project.id}`} className="ed-link">
											Open project
										</Link>
										{lease.project.pending && <PendingTag />}
									</span>
								)}
								{lease.lease_agreement_id && (
									<Link to={`/lease-agreements/${lease.lease_agreement_id}`} className="ed-link">
										Open lease
									</Link>
								)}
							</div>
						</li>
					))}
				</ol>
			)}
		</React.Fragment>
	);
};

export default UnitDrawer;
